import { Location } from '@angular/common';
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import MobileDetect from 'mobile-detect';
import { ManageLocationsService } from '../account/services/manage-locations.service';
import { AppState } from '../app.state';
import { SharedService } from '../core/services/shared.service';
import { StorageService } from '../core/services/storage/storage.service';
import { NotificationType } from '../shared/models/notification-type';
import { ErrorHandlerService } from '../shared/services/error-handler/error-handler.service';
import { NotificationService } from '../shared/services/notification/notification.service';
import { User } from '../shared/services/user/models/user.model';
import { UserService } from '../shared/services/user/user.service';
import { ValidationService } from '../shared/services/validation/validation.service';
import { Estimator } from '../ship/models/estimator.model';
import { Package } from '../ship/models/package.model';
import { ShipmentService } from '../ship/services/shipment.service';
import { CostEstimate, ShipFrom, ShipTo } from './models/estimate.model';
import { CostEstimatorService } from './services/cost-estimator.service';

@Component({
    selector: 'upsc-cost-estimator',
    templateUrl: './cost-estimator.component.html',
    styleUrls: ['./cost-estimator.component.scss'],
})
export class CostEstimatorComponent implements OnInit {
    @ViewChild('costEstimator') costEstimator: ElementRef;
    @ViewChild('upsServicesPanel') upsServicesPanel: MatExpansionPanel;
    @ViewChild('fedexServicesPanel') fedexServicesPanel: MatExpansionPanel;

    public formGroup: UntypedFormGroup;
    public estimator = true;
    public serviceList = false;
    public saturdayPickup = false;
    public saturdayDelivery = false;
    public codService = false;
    public isCustomPackaging = false;
    // public residentialAddress = false;
    public shipFromAddress = '0';
    // public shipToAddress = '0';
    public shipFromLocations: any[];
    public countriesList: any[];
    public upsServices: any[];
    public fedexServices: any[];
    public customerServices: any[];
    public defaultLocation: string;
    public user: User;
    public isInProgress = false;
    public selectedEstimateId: string;
    public responsePackage: Package;
    public isZipMandatory = true;
    public currencyCode = 'USD';
    public maxWeight = 125;
    public minWeight = 0;
    public maxInsuredValue = 0;
    public minInsuredValue = 0;
    public weightUOM = 'LBS';
    public dimensionUnit = 'inches';
    public isUpsServicesClosed = false;
    public isFedExServicesClosed = false;
    public isUpsServiceSelected = false;
    public isFedExServiceSelected = false;
    public selectedEstimator: Estimator;
    public isMobile = false;
    private defaultMaxWeight = 125;
    private md = new MobileDetect(window.navigator.userAgent);

    public constructor(private manageLocationsService: ManageLocationsService,
                       public costEstimate: CostEstimate,
                       private costEstimatorService: CostEstimatorService,
                       private formBuilder: UntypedFormBuilder,
                       private notificationService: NotificationService,
                       private _location: Location,
                       private router: Router,
                       private storageService: StorageService,
                       private shipmentService: ShipmentService,
                       private readonly appState: AppState,
                       @Inject(MAT_DIALOG_DATA) public data: any,
                       private sharedService: SharedService,
                       private translateService: TranslateService,
                       private userService: UserService,
                       private validationService: ValidationService,
                       private errorHandlerService: ErrorHandlerService,
                       public dialogRef: MatDialogRef<CostEstimatorComponent>,
    ) {
        this.user = this.appState.user$();
        this.currencyCode = !this.userService.isDEGBUser(this.user)
            ? 'USD'
            : (this.userService.isDEUser(this.user)
                ? 'EUR'
                : 'GBP');
        this.defaultLocation = this.user?.DefaultLocation;

        this.costEstimate.ShipFrom = new ShipFrom();
        this.costEstimate.ShipTo = new ShipTo();
        this.getLocations();
        this.getCountries();
        this.isMobile = !!this.md.mobile();
    }

    mapFormValueToCostEstimate(formValue) {
        this.costEstimate.ShipTo.Country = formValue.ShipToCountry;
        this.costEstimate.ShipTo.Zip = formValue.PostalCode;
        this.costEstimate.InsuredValue = formValue.InsuredValue;
        this.costEstimate.Weight = formValue.Weight;
        this.costEstimate.ShipTo.IsResidential = formValue.ShipToResidential;
        this.costEstimate.IsSaturdayPickUp = formValue.IsSaturdayPickUp;
        this.costEstimate.IsSaturdayDelivery = formValue.IsSaturdayDelivery;
        this.costEstimate.IsCOD = formValue.IsCOD;
        this.costEstimate.CodAmount = formValue.CodAmount;
        this.costEstimate.IsSecuredCOD = formValue.IsSecuredCOD;
        this.costEstimate.Height = formValue.Height;
        this.costEstimate.Width = formValue.Width;
        this.costEstimate.Length = formValue.Length;
        this.costEstimate.IsRegularPickUp = formValue.PickupOption === '1' || formValue.PickupOption === '3';
        this.costEstimate.IsDropoff = formValue.PickupOption === '2';
        this.costEstimate.SelectedServiceCode = formValue.selectedServiceCode;
    }

    initFormGroup() {
        this.formGroup = this.formBuilder.group({
            ShipFromAddress: [this.defaultLocation],
            ShipToCountry: [''],
            PostalCode: [''],
            InsuredValue: [
                '',
                Validators.compose([Validators.required, Validators.min(this.minInsuredValue), this.validationService.isInteger()])],
            Weight: ['', Validators.compose([Validators.required, Validators.min(0.01), Validators.max(this.maxWeight)])],
            Length: ['0'],
            Width: ['0'],
            Height: ['0'],
            ShipToResidential: [false],
            IsSaturdayPickUp: [false],
            IsSaturdayDelivery: [false],
            IsCOD: [false],
            IsSecuredCOD: [false],
            IsCustomPackaging: [false],
            CodAmount: ['0'],
            PickupOption: ['2'],
            SelectedServiceCode: [''],
        });
        this.setWeightValidation(this.maxWeight);
    }

    ngOnInit() {
        this.shipFromAddress = this.defaultLocation;
        this.initFormGroup();
    }

    getLocations() {
        this.manageLocationsService.getLocations().subscribe(
            (locations) => {
                if (locations) {
                    this.shipFromLocations = locations;
                }
                this.populateShipFrom(this.shipFromAddress);
            },
            (error) => {
                this.notificationService.notify(
                    this.errorHandlerService.getHttpErrorMessage(error),
                    this.translateService.instant('costEstimator.ShipFromLocationsLoadingError_msg'),
                    NotificationType.ERROR);
            });
    }

    getCountries() {
        this.manageLocationsService.getCountries().subscribe(
            (countries) => {
                if (countries) {
                    this.countriesList = _.sortBy(countries, ['CountryName']);
                    this.setShipToCountry();
                }
            },
            (error) => {
                this.notificationService.notify(
                    this.errorHandlerService.getHttpErrorMessage(error),
                    this.translateService.instant('CountriesLoadingError_msg'),
                    NotificationType.ERROR);
            });
    }

    setShipToCountry() {
        if (this.countriesList.indexOf(this.user.CountryCode)) {
            this.formGroup.controls['ShipToCountry'].setValue(this.user.CountryCode);
        }
        this.setMaxInsuredValue(this.user.CountryCode);
    }

    setMaxInsuredValue(countryCode) {
        if (countryCode) {
            this.costEstimatorService.getCustomerServices(countryCode).subscribe(
                (response) => {
                    this.handleGetCustomerServicesSuccess(response);
                },
                (error) => {
                    this.handleGetCustomerServicesFailure(error);
                });
        }
    }

    handleGetCustomerServicesSuccess(response) {
        if (response.length > 0) {
            const array = this.customerServices = response;
            if (this.formGroup.controls['ShipToCountry'].value.toLowerCase() === 'us') {
                this.maxInsuredValue = array.reduce((max,
                                                     p,
                ) => p.MaxCoverage > max ? p.MaxCoverage : max, array[0].MaxCoverage);
            } else {
                this.maxInsuredValue = array.reduce((min,
                                                     p,
                ) => p.CountryMax < min ? p.CountryMax : min, array[0].CountryMax);
            }
            this.setInsuredValueValidation(this.maxInsuredValue);
        }
    }

    handleGetCustomerServicesFailure(error) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(error),
            this.translateService.instant('costEstimator.errorLoadingCustomerServices'),
            NotificationType.ERROR);
    }

    public setCOD() {
        this.formGroup.controls['CodAmount'].setValue('0');
        this.formGroup.controls['IsSecuredCOD'].setValue(false);
    }

    customPackaging() {
        this.formGroup.controls['Height'].setValue('0');
        this.formGroup.controls['Width'].setValue('0');
        this.formGroup.controls['Length'].setValue('0');
    }

    // [MV3-2519] Change weight calculation spec. to follow the one on the Ship page.
    public calculateWeight() {
        const length = this.formGroup.controls.Length.value;
        const width = this.formGroup.controls.Width.value;
        const height = this.formGroup.controls.Height.value;
        const isDimensionsReady = length
            && !isNaN(length)
            && width
            && !isNaN(width)
            && height
            && !isNaN(height);

        if (!isDimensionsReady) {
            return;
        }

        this.shipmentService.getPackageWeight(length, width, height, this.dimensionUnit, this.user)
            .subscribe(
                weight => this.handleGetPackageWeightSuccess(weight),
                err => this.handleGetPackageWeightFailure(err),
            );
    }

    onSubmit(event, form) {
        this.isInProgress = true;
        // calculating the weight again, in case the user forcefully inputted low weight
        if (this.isCustomPackaging) {
            this.calculateWeight();
        }
        this.mapFormValueToCostEstimate(form);

        this.costEstimatorService.getRates(this.costEstimate).subscribe(
            (response) => {
                this.isInProgress = false;

                this.responsePackage = response;
                this.upsServices = response.Estimator.filter(x => x.CarrierCode.toLowerCase() === 'ups');
                this.fedexServices = response.Estimator.filter(x => x.CarrierCode.toLowerCase() === 'fedex');
                this.estimator = false;
                this.serviceList = true;
            },
            (error) => {
                this.isInProgress = false;
                this.notificationService.notify(
                    this.errorHandlerService.getHttpErrorMessage(error),
                    this.translateService.instant('costEstimator.ErrorGettingRates'),
                    NotificationType.ERROR);
            });
    }

    onToCountryChange(toCountry) {
        const countryObject = this.countriesList.filter(x => x.CountryCode === toCountry);
        if (countryObject && countryObject.length > 0) {
            this.isZipMandatory = countryObject[0].IsPostalCodeAware;
            this.clearZipCode();
        }
        this.setMaxInsuredValue(toCountry);
        this.clearIsResidential();
    }

    // calculateWeight() {
    //   let divideBy = 1;
    //   let weight = 0;
    //   if (this.formGroup.controls['ShipToCountry'].value.toLowerCase() === 'us' &&
    //       this.costEstimate.ShipFrom.Country.toLowerCase() === 'us') {
    //     divideBy = 166;
    //   } else {
    //     divideBy = 139;
    //   }
    //
    //   weight = (Number(this.formGroup.controls['Height'].value)
    // * Number(this.formGroup.controls['Width'].value) * Number(this.formGroup.controls['Length'].value) / divideBy);
    //   if ((weight > Number(this.formGroup.controls['Weight'].value))) { // bigger weight should be considered {
    //     this.formGroup.controls['Weight'].setValue(Math.round(weight));
    //   }
    // }

    clearZipCode() {
        this.formGroup.controls.PostalCode.setValue('');
    }

    clearIsResidential() {
        this.formGroup.controls.ShipToResidential.setValue(false);
    }

    populateShipFrom(shipFromContact) {
        let selectedAddress: ShipFrom;

        if (shipFromContact) {
            selectedAddress = this.shipFromLocations.filter(x => x.ContactId === shipFromContact)?.[0];
        } else {
            // Select default.
            selectedAddress = this.shipFromLocations.filter(address => address.ContactId === this.appState.user$()?.DefaultLocation)?.[0];
        }

        this.costEstimate.ShipFrom = selectedAddress;
        this.formGroup.get('ShipFromAddress').setValue(selectedAddress?.ContactId);
        const isUSCAAddress = ['US', 'CA'].includes(selectedAddress?.Country);
        this.weightUOM = isUSCAAddress ? 'LBS' : 'KGS';
        this.dimensionUnit = isUSCAAddress ? 'inches' : 'cm';
        this.maxWeight = isUSCAAddress ? 125 : 70;
        this.setWeightValidation(this.maxWeight);
    }

    reset() {
        this.costEstimate = new CostEstimate();
        this.costEstimate.ShipFrom = new ShipFrom();
        this.costEstimate.ShipTo = new ShipTo();
        this.initFormGroup();
        this.setShipToCountry();
        this.populateShipFrom(this.formGroup.controls.ShipFromAddress.value);
    }

    public onEstimateChanged(estimator: Estimator) {
        this.selectedEstimateId = estimator.EstimatorHeaderID;
        this.formGroup.controls.SelectedServiceCode.setValue(estimator.ServiceCode);
        this.shipNow(null);
    }

    public shipNow(event) {
        if (event) {
            event.preventDefault();
        }

        if (this.dialogRef) {
            this.dialogRef.close();
        }

        if (!this.responsePackage.Estimator || !this.responsePackage.Estimator.length) {
            return;
        }

        this.storageService.set('cost-estimate', this.formGroup.value);
        this.storageService.set('estimate-package', this.responsePackage);
        this.storageService.set('selected-estimate', this.selectedEstimateId);

        const estimate = this.responsePackage.Estimator.find(est => est.EstimatorHeaderID === this.selectedEstimateId);
        const carrierCode = estimate.CarrierCode.toLowerCase();
        const shipPageUrl = `/ship/${ carrierCode }`;
        const serviceCode = estimate.ServiceCode;

        this.router.navigate([shipPageUrl]);
    }

    public onEstimateRadioChanged(selectedEstimateId: string) {
        const allEstimators: Estimator[] = [...this.upsServices, ...this.fedexServices];
        this.selectedEstimator = allEstimators.find(item => item.EstimatorHeaderID === selectedEstimateId);

        if (!this.selectedEstimator) {
            return;
        }

        this.selectedEstimateId = this.selectedEstimator.EstimatorHeaderID;
        this.formGroup.controls.SelectedServiceCode.setValue(this.selectedEstimator.ServiceCode);

        this.isUpsServiceSelected = this.selectedEstimator.CarrierCode.toLowerCase() === 'ups';
        this.isFedExServiceSelected = this.selectedEstimator.CarrierCode.toLowerCase() === 'fedex';

        if (this.md.mobile()) {
            this.upsServicesPanel.close();
            this.fedexServicesPanel.close();
        }
    }

    public onUpsServicesOpened() {
        this.isUpsServicesClosed = false;
        this.isMobile = !!this.md.mobile();
    }

    public onUpsServicesClosed() {
        this.isUpsServicesClosed = true;
        this.isMobile = !!this.md.mobile();
    }

    public onFedExServicesOpened() {
        this.isFedExServicesClosed = false;
        this.isMobile = !!this.md.mobile();
    }

    public onFedExServicesClosed() {
        this.isFedExServicesClosed = true;
        this.isMobile = !!this.md.mobile();
    }

    private setInsuredValueValidation(maxInsuredValue: number) {
        if (!this.formGroup) {
            return;
        }

        const weightValidators = [
            Validators.required,
            Validators.min(this.minInsuredValue),
            Validators.max(maxInsuredValue),
            this.validationService.isInteger(),
        ];

        this.validationService.setFormControlValidators(
            this.formGroup.controls.InsuredValue,
            Validators.compose(weightValidators),
        );
    }

    private handleGetPackageWeightSuccess(weight) {
        this.maxWeight = this.defaultMaxWeight;
        this.minWeight = weight;

        // [MV3-1388] The system shall allow the user to input a weight equal to or greater than the calculated dimensional weight,
        // but not greater than the maximum allowed weight (125 LBS or 70 KGS).
        // if (this.formGroup.controls.packageType.value !== '02') {
        //   this.minWeight = this.weightUOM === 'KGS' ? 0 : 1;
        // }

        this.setWeightValidation(this.maxWeight);

        this.formGroup.patchValue({
            Weight: weight,
        });

        this.formGroup.controls.Weight.markAsDirty();
        this.formGroup.controls.Weight.markAsTouched();
    }

    private handleGetPackageWeightFailure(err) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            'Failed getting package weight',
            NotificationType.ERROR);
    }

    private setWeightValidation(maxWeight: number) {
        if (!this.formGroup) {
            return;
        }

        const weightValidators = [
            Validators.required,
            Validators.min(this.minWeight),
            Validators.max(maxWeight),
        ];

        if (this.weightUOM === 'LBS') {
            weightValidators.push(Validators.pattern('^[0-9]*$'));
        }

        this.validationService.setFormControlValidators(
            this.formGroup.controls.Weight,
            Validators.compose(weightValidators),
        );
    }
}
